import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, ExclamationTriangleIcon, ChartBarIcon, UserIcon, CogIcon, BellIcon, EyeIcon, ClockIcon } from '@heroicons/react/24/outline';

const UserDashboard = () => {
  const [userStats, setUserStats] = useState({
    complianceScore: 0,
    totalViolations: 0,
    aiInteractions: 0,
    lastViolation: null
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [personalViolations, setPersonalViolations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      // TODO: Replace with actual API calls
      // const response = await fetch('/api/user/dashboard');
      // const data = await response.json();
      
      // Mock data for now
      setUserStats({
        complianceScore: 92,
        totalViolations: 2,
        aiInteractions: 156,
        lastViolation: '2024-08-18T10:30:00Z'
      });

      setRecentActivity([
        {
          id: 1,
          platform: 'ChatGPT',
          interaction: 'Code review assistance',
          timestamp: '2024-08-20T15:45:00Z',
          compliance: 'clean'
        },
        {
          id: 2,
          platform: 'Claude',
          interaction: 'Document analysis',
          timestamp: '2024-08-20T14:20:00Z',
          compliance: 'clean'
        },
        {
          id: 3,
          platform: 'Gemini',
          interaction: 'Research query',
          timestamp: '2024-08-20T13:10:00Z',
          compliance: 'clean'
        }
      ]);

      setPersonalViolations([
        {
          id: 1,
          type: 'PII Detection',
          severity: 'medium',
          timestamp: '2024-08-18T10:30:00Z',
          platform: 'ChatGPT',
          description: 'Potential email address in prompt',
          resolved: true
        }
      ]);

      setUserProfile({
        name: 'John Doe',
        email: 'john.doe@company.com',
        department: 'Engineering',
        role: 'Senior Developer',
        lastLogin: '2024-08-20T16:00:00Z'
      });

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setIsLoading(false);
    }
  };

  const getComplianceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getComplianceStatus = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Needs Attention';
  };

  const getViolationSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Dashboard</h1>
              <p className="text-sm text-gray-600">Monitor your AI compliance and usage</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <UserIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">{userProfile?.name}</span>
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <BellIcon className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <CogIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Compliance Score</p>
                <p className={`text-2xl font-bold ${getComplianceColor(userStats.complianceScore)}`}>
                  {userStats.complianceScore}%
                </p>
                <p className="text-xs text-gray-500">{getComplianceStatus(userStats.complianceScore)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Violations</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.totalViolations}</p>
                <p className="text-xs text-gray-500">This month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">AI Interactions</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.aiInteractions}</p>
                <p className="text-xs text-gray-500">This month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ClockIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Last Violation</p>
                <p className="text-lg font-bold text-gray-900">
                  {userStats.lastViolation ? new Date(userStats.lastViolation).toLocaleDateString() : 'None'}
                </p>
                <p className="text-xs text-gray-500">Clean streak</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent AI Activity</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {activity.platform.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.interaction}</p>
                      <p className="text-sm text-gray-500">{activity.platform}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Personal Violations */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Your Violations</h3>
            </div>
            <div className="p-6">
              {personalViolations.length === 0 ? (
                <div className="text-center py-8">
                  <ShieldCheckIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500">No violations detected</p>
                  <p className="text-sm text-gray-400">Keep up the good work!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {personalViolations.map((violation) => (
                    <div key={violation.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getViolationSeverityColor(violation.severity)}`}>
                          {violation.severity}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(violation.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 mb-1">{violation.type}</h4>
                      <p className="text-sm text-gray-600 mb-2">{violation.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{violation.platform}</span>
                        {violation.resolved && (
                          <span className="text-xs text-green-600 font-medium">✓ Resolved</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compliance Tips */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">💡 Compliance Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>Never share company trade secrets or confidential information with AI</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>Avoid including PII (names, emails, phone numbers) in your prompts</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>Use generic examples instead of real customer data</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>Review AI responses before sharing them externally</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
